import type { Awareness } from "y-protocols/awareness";
import { getAwareness } from "./yjsDoc";

export interface PresenceUser {
  name: string;
  color: string;
  clientId: number;
}

/** Set the local user's presence info on the awareness instance. */
export function setPresence(user: { name: string; color: string }): void {
  const awareness = getAwareness();
  if (!awareness) return;
  awareness.setLocalStateField("user", {
    name: user.name,
    color: user.color,
  });
}

/** Get all connected users (excluding those with no state). */
export function getPresences(): PresenceUser[] {
  const awareness = getAwareness();
  if (!awareness) return [];
  const users: PresenceUser[] = [];
  awareness.getStates().forEach((state, clientId) => {
    if (state.user) {
      users.push({
        name: state.user.name,
        color: state.user.color,
        clientId,
      });
    }
  });
  return users;
}

/** Subscribe to presence changes. Returns an unsubscribe function. */
export function onPresenceChange(cb: () => void): () => void {
  const awareness = getAwareness();
  if (!awareness) return () => {};
  const handler = () => cb();
  awareness.on("change", handler);
  return () => awareness.off("change", handler);
}

/** Broadcast camera state for follow mode. */
export function setCameraState(position: [number, number, number], target: [number, number, number]): void {
  const awareness = getAwareness();
  if (!awareness) return;
  awareness.setLocalStateField("camera", { position, target });
}

/** Get a specific client's camera state. */
export function getCameraState(clientId: number): { position: [number, number, number]; target: [number, number, number] } | null {
  const awareness = getAwareness();
  if (!awareness) return null;
  const state = awareness.getStates().get(clientId);
  return state?.camera ?? null;
}

/** Generate a deterministic color from a user name. */
export function colorForName(name: string): string {
  const palette = [
    "#58a6ff", "#f78166", "#3ddc84", "#d2a8ff",
    "#f0a030", "#ff7eb6", "#79c0ff", "#56d4dd",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return palette[Math.abs(hash) % palette.length];
}
